import { useState, useEffect, useCallback } from "react";
import { Joyride, STATUS } from "react-joyride";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";

const VENDOR_TOUR_STEPS = [
  {
    target: '[data-testid="vendor-dashboard-header"]',
    title: "Welcome to BirthdayDeaLight Vendor Mode",
    content: "Everything here is built for food truck owners. This is your command center for BirthdayDeaLight.",
    disableBeacon: true,
    placement: "center",
  },
  {
    target: '[data-testid="vendor-nav-go-live"]',
    title: "Your Most Important Button",
    content: "Tap this when you are ready to serve. Your followers get notified instantly. One tap and your community knows you are open.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="profile-completeness-bar"]',
    title: "Complete Your Profile",
    content: "A complete profile gets found more often. Each section you fill in brings more birthday celebrants to your truck.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="vendor-nav-analytics"]',
    title: "See Who Is Finding You",
    content: "Your analytics show you how many people found your truck, followed you, and claimed your birthday offer. The data grows with every customer.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="birthday-special-card"]',
    title: "Set Up Your Birthday Offer",
    content: "A birthday offer costs you one item. The group it brings pays full price. This is the most powerful thing you can do on this platform.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="punch-card-section-card"]',
    title: "Build Customer Loyalty",
    content: "Your loyalty punch card keeps customers coming back. Configure how many visits earn a reward and what that reward is.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="qr-code-section-card"]',
    title: "Print Your QR Codes",
    content: "Print these and put them on your truck window and service counter. Customers scan them to follow you and earn loyalty stamps.",
    disableBeacon: true,
  },
  {
    target: '[data-testid="vendor-nav-settings"]',
    title: "You Are All Set",
    content: "Everything else lives in Settings — your schedule, your POS connection, your photos, your social links. Come back to the tour anytime from Settings if you need a refresher. Tap Finish to begin.",
    disableBeacon: true,
  },
];

const TOOLTIP_STYLES = {
  options: {
    zIndex: 10000,
    arrowColor: "#1C1C1E",
    backgroundColor: "#1C1C1E",
    overlayColor: "rgba(0,0,0,0.65)",
    textColor: "#fff",
    primaryColor: "#F59E0B",
  },
  tooltip: {
    borderRadius: "16px",
    padding: "20px",
    border: "2px solid #F59E0B",
  },
  tooltipTitle: {
    color: "#F59E0B",
    fontSize: "16px",
    fontWeight: 800,
  },
  tooltipContent: {
    color: "#e2e8f0",
    fontSize: "13px",
    lineHeight: "1.6",
  },
  buttonNext: {
    backgroundColor: "#F59E0B",
    color: "#1C1C1E",
    fontWeight: 700,
    borderRadius: "10px",
    fontSize: "13px",
  },
  buttonBack: {
    color: "#94a3b8",
    fontSize: "12px",
  },
  buttonSkip: {
    color: "#94a3b8",
    fontSize: "12px",
  },
  buttonClose: {
    color: "#94a3b8",
  },
  spotlight: {
    borderRadius: "12px",
    border: "2px solid #F59E0B",
  },
};

export default function VendorJoyrideTour({ run, onComplete }) {
  const handleCallback = useCallback((data) => {
    const { status, action } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === "close") {
      onComplete?.();
    }
  }, [onComplete]);

  if (!run) return null;

  return (
    <Joyride
      steps={VENDOR_TOUR_STEPS}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableOverlayClose
      spotlightClicks={false}
      callback={handleCallback}
      styles={TOOLTIP_STYLES}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip Tour",
      }}
    />
  );
}

export function useVendorTour() {
  const { user } = useAuth();
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.is_test_account) return;
    if (user.vendor_joyride_completed) return;
    if (user.role !== "vendor") return;
    // Small delay for DOM to mount
    const timer = setTimeout(() => setRunTour(true), 1500);
    return () => clearTimeout(timer);
  }, [user]);

  const completeTour = useCallback(async () => {
    setRunTour(false);
    try {
      await axios.put(`${API}/vendor/tour-complete`);
    } catch { /* silent */ }
  }, []);

  const replayTour = useCallback(() => {
    setRunTour(true);
  }, []);

  return { runTour, completeTour, replayTour };
}
